import {
    PriceDataItem,
    PriceSubscriptionRequestPayload,
    SidecarApi,
    SidecarStreamRequest,
} from '@instinet-bcng/sidecar-client';
import { SidecarRequestManager } from './SidecarRequestManager';
import {
    buildLogger,
    Logger,
    LoggingService,
} from '@instinet-analytics/core-lib';
import {
    BloombergMarketDataRequest,
    MarketDataTickRequest,
} from 'marketData/MarketDataRequest';
import { v4 as uuid } from 'uuid';
import { TickDataReceivedHandler } from 'marketData/ExternalMarketDataStrategy';
import { toTickItem } from 'app/openFin/sidecar/requests/mapping';
import { addRequest } from 'app/glue/bloomberg/store/bloombergSlice';
import { ActionsDispatcher } from 'store/ActionsDispatcher';

export class SidecarTickRequestManager extends SidecarRequestManager<
    MarketDataTickRequest & BloombergMarketDataRequest
> {
    private logger: Logger;

    private response?: SidecarStreamRequest<
        PriceSubscriptionRequestPayload,
        PriceDataItem
    >;
    private onData: TickDataReceivedHandler;

    public constructor(options: {
        request: MarketDataTickRequest & BloombergMarketDataRequest;
        api: SidecarApi;
        loggingService: LoggingService;
        dispatcher: ActionsDispatcher;
        onData: TickDataReceivedHandler;
    }) {
        super(options);

        this.logger = buildLogger(
            'SidecarTickRequestManager',
            options.loggingService
        );
        this.onData = options.onData;
    }

    public async subscribe() {
        const correlationId = uuid();
        const payload = {
            symbol: this.request.bbSymbol,
            correlationId: correlationId,
            intervalInSeconds: 1,
        };

        this.logger.debug('Subscribing to prices', payload);

        this.response = await this.api.subscribeToPrices({
            payload,
            onData: this.handleDataReceived.bind(this),
            onError: (err) => {
                this.logger.critical('Error from price subscription', err);
            },
        });

        this.dispatch(
            addRequest({
                uid: correlationId,
                requestType: 'subscribeToPrices',
                request: this.request,
                args: payload,
            })
        );
    }

    public async unsubscribe(): Promise<void> {
        await this.response?.unsubscribe();
    }

    private handleDataReceived(item: PriceDataItem) {
        this.onData([toTickItem(this.request.bbSymbol, item)]);
    }
}
